'use strict';

import React, {
    useCallback,
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {
    Button,
    ButtonProps
} from 'primereact/button';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';


type InputProps = Omit<ButtonProps, 'onClick' | 'key'>;
const inputProps = keys<InputProps>();

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    inputClassNames: [],
    inputStyle     : {}
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    /**
     * The label to show the button with.
     */
    buttonLabel? : string;

    /**
     * Fired on click
     *
     * @param originalEvent
     */
    onTriggerAction : ( originalEvent : any ) => void;

    pt?: {
        fieldWrapper?: FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ButtonField = React.forwardRef<Button, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  buttonLabel,
                  id,
                  inputClassNames,
                  inputStyle,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onTriggerAction,
                  pt
              }               = props,
              elementId       = id ?? `${uid}_buttonField`,
              messageResolver = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : messageTemplateStringResolver( __( messageKey, messagePlaceholders ) );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              onClick         = useCallback(
                  ( event : React.MouseEvent<HTMLButtonElement> ) => {
                      onTriggerAction( event );
                  },
                  [
                      onTriggerAction
                  ]
              ),
              onMouseDown     = useCallback(
                  () => {
                      document.getElementById( elementId )?.classList.add( 'yn-button-pressed' );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onMouseUp       = useCallback(
                  () => {
                      document.getElementById( elementId )?.classList.remove( 'yn-button-pressed' );
                  },
                  [
                      'onlyOnce'
                  ]
              );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            <Button
                {..._.pick( props, inputProps ) as InputProps}

                key={elementId}
                id={elementId}

                ref={forwardedRef}

                onClick={onClick}

                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseOut={onMouseUp}

                label={messageResolver( buttonLabel ?? '' )}

                className={`yn-buttonfield p-button-text p-button-raised ${inputClassNames.join( ' ' )}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle }}

                tooltip={undefined}
                tooltipOptions={undefined}
            />
        </FieldWrapper>;
    }
} );
ButtonField.defaultProps = defaultProps;
ButtonField.displayName = 'ButtonField';

export default ButtonField;
